import React, { useState, useEffect } from 'react';
import axios from 'axios';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Clock, ChevronLeft, ChevronRight, LogIn, LogOut, Coffee, Play, Calendar, Timer
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

import API from '@/config/api';
// API imported from config;

const MyHoursPage = () => {
  const { user, getAuthHeaders } = useAuth();
  const [weeklyData, setWeeklyData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [weekStart, setWeekStart] = useState(() => {
    const today = new Date();
    const sunday = new Date(today);
    sunday.setDate(today.getDate() - today.getDay());
    return sunday.toISOString().split('T')[0];
  });

  useEffect(() => {
    fetchWeeklyHours();
  }, [weekStart]);

  const fetchWeeklyHours = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API}/timeclock/my-hours/weekly?week_start=${weekStart}`,
        getAuthHeaders()
      );
      setWeeklyData(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load hours');
    } finally {
      setLoading(false);
    }
  };

  const changeWeek = (weeks) => {
    const date = new Date(weekStart);
    date.setDate(date.getDate() + (weeks * 7));
    setWeekStart(date.toISOString().split('T')[0]);
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '-';
    return new Date(timestamp).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getPunchIcon = (type) => {
    const icons = {
      clock_in: <LogIn className="w-4 h-4 text-green-500" />,
      clock_out: <LogOut className="w-4 h-4 text-red-500" />,
      break_start: <Coffee className="w-4 h-4 text-amber-500" />,
      break_end: <Play className="w-4 h-4 text-blue-500" />
    };
    return icons[type] || <Clock className="w-4 h-4" />;
  };

  const getPunchLabel = (type) => {
    const labels = {
      clock_in: 'Clock In',
      clock_out: 'Clock Out',
      break_start: 'Break Start',
      break_end: 'Break End'
    };
    return labels[type] || type;
  };

  const getDayName = (dateStr) => {
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
  };

  // Generate all days of the week
  const getWeekDays = () => {
    if (!weeklyData) return [];
    
    const days = [];
    const start = new Date(weeklyData.week_start);
    
    for (let i = 0; i < 7; i++) {
      const date = new Date(start);
      date.setDate(start.getDate() + i);
      const dateStr = date.toISOString().split('T')[0];
      
      // Find data for this day
      const dayData = weeklyData.daily_hours.find(d => d.date === dateStr);
      
      days.push({
        date: dateStr,
        dayName: date.toLocaleDateString('en-US', { weekday: 'short' }),
        fullDayName: date.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' }),
        work_hours: dayData?.work_hours || 0,
        break_hours: dayData?.break_hours || 0,
        punches: dayData?.punches || []
      });
    }
    
    return days;
  };

  if (loading) {
    return (
      <DashboardLayout title="My Hours">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  const weekDays = getWeekDays();

  return (
    <DashboardLayout title="My Hours">
      <div className="space-y-6 max-w-4xl mx-auto" data-testid="my-hours-page">
        
        {/* Summary Card */}
        <Card className="border-2">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-xl font-heading flex items-center gap-2">
                <Timer className="w-5 h-5 text-primary" />
                Weekly Summary
              </CardTitle>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="icon" onClick={() => changeWeek(-1)}>
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="text-sm px-3 min-w-[180px] text-center">
                  {weeklyData?.week_start} - {weeklyData?.week_end}
                </span>
                <Button variant="outline" size="icon" onClick={() => changeWeek(1)}>
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <div className="p-6 bg-primary/5 rounded-xl text-center">
                <p className="text-sm text-muted-foreground mb-1">Total Hours Worked</p>
                <p className="text-4xl font-bold text-primary">{weeklyData?.total_work_hours || 0}h</p>
              </div>
              <div className="p-6 bg-amber-50 rounded-xl text-center">
                <p className="text-sm text-muted-foreground mb-1">Total Break Time</p>
                <p className="text-4xl font-bold text-amber-600">{weeklyData?.total_break_hours || 0}h</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Daily Hours Grid */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-primary" />
              Daily Breakdown
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-7 gap-2 mb-4">
              {weekDays.map((day, idx) => (
                <div 
                  key={idx} 
                  className={`p-3 rounded-lg text-center ${
                    day.work_hours > 0 ? 'bg-green-50 border border-green-200' : 'bg-muted'
                  }`}
                >
                  <p className="text-xs font-medium text-muted-foreground">{day.dayName}</p>
                  <p className="text-lg font-bold">
                    {day.work_hours > 0 ? `${day.work_hours}h` : '-'}
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Detailed Punches */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-primary" />
              Punch Details
            </CardTitle>
          </CardHeader>
          <CardContent>
            {weekDays.some(d => d.punches.length > 0) ? (
              <div className="space-y-4">
                {weekDays.filter(d => d.punches.length > 0).map((day, idx) => (
                  <div key={idx} className="border rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <p className="font-medium">{day.fullDayName}</p>
                      <div className="flex items-center gap-2">
                        <Badge variant="default">{day.work_hours}h worked</Badge>
                        {day.break_hours > 0 && (
                          <Badge variant="outline">{day.break_hours}h break</Badge>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {day.punches
                        .sort((a, b) => a.timestamp.localeCompare(b.timestamp))
                        .map((punch, pIdx) => (
                          <div 
                            key={pIdx} 
                            className="flex items-center gap-2 px-3 py-2 bg-muted rounded-lg"
                          >
                            {getPunchIcon(punch.punch_type)}
                            <span className="text-sm">{getPunchLabel(punch.punch_type)}</span>
                            <span className="font-mono text-sm">{formatTime(punch.timestamp)}</span>
                          </div>
                        ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <Clock className="w-12 h-12 mx-auto mb-2 opacity-30" />
                <p>No punches recorded for this week</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default MyHoursPage;
